import React, { useState, useEffect, useMemo } from 'react';
import './MonthPlan.css';
import Viaje from './Viaje';
import StyledTextarea from './StyledTextarea';
import { COUNTRIES } from './CountrySelector';
import { saveMonthData, loadMonthData, getDaysInMonth, getFirstDayOfMonth } from '../utils/storage';

const DAYS_OF_WEEK = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const MonthPlan = ({ monthIndex, monthName, year }) => {
  const [data, setData] = useState({
    objetivoViajero: '',
    calendario: {},
    viajes: [],
    ideasFuturas: ''
  });

  useEffect(() => {
    const savedData = loadMonthData(monthIndex, year);
    if (savedData && savedData.plan) {
      setData({
        objetivoViajero: savedData.plan.objetivoViajero || '',
        calendario: savedData.plan.calendario || {},
        viajes: savedData.plan.viajes || [],
        ideasFuturas: savedData.plan.ideasFuturas || '',
        eventToViajeIndexMap: savedData.plan.eventToViajeIndexMap || {}
      });
    } else if (savedData && !savedData.plan) {
      // Compatibilidad con datos antiguos
      setData({
        ...savedData,
        eventToViajeIndexMap: savedData.eventToViajeIndexMap || {}
      });
    }
  }, [monthIndex, year]);

  // Regenerar el mapeo cuando cambian los viajes y no hay mapeo
  useEffect(() => {
    if (data.viajes && data.viajes.length > 0 && (!data.eventToViajeIndexMap || Object.keys(data.eventToViajeIndexMap).length === 0)) {
      updateCalendarEvents(data);
    }
  }, [data.viajes.length, year]); // Solo cuando cambia la cantidad de viajes o el año

  const handleChange = (field, value) => {
    const newData = { ...data, [field]: value };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedData = { ...monthData, plan: newData };
    saveMonthData(monthIndex, updatedData, year);
  };

  const handleViajeChange = (viajeIndex, updatedViaje) => {
    const viajes = [...data.viajes];
    viajes[viajeIndex] = updatedViaje;
    const newData = { ...data, viajes };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedMonthData = { ...monthData, plan: newData };
    saveMonthData(monthIndex, updatedMonthData, year);
    updateCalendarEvents(newData); // Update calendar when a trip changes
  };

  const addViaje = () => {
    // Inicializar con el día 01 del mes actual
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    const fechaInicio = firstDayOfMonth.toISOString().split('T')[0];
    const fechaFin = firstDayOfMonth.toISOString().split('T')[0];
    
    const nuevoViaje = {
      destino: '',
      fechaInicio: fechaInicio,
      fechaFin: fechaFin,
      vuelos: [],
      airbnbs: []
    };
    const viajes = [...data.viajes, nuevoViaje];
    const newData = { ...data, viajes };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedMonthData = { ...monthData, plan: newData };
    saveMonthData(monthIndex, updatedMonthData, year);
  };

  const deleteViaje = (viajeIndex) => {
    const viajes = data.viajes.filter((_, i) => i !== viajeIndex);
    const newData = { ...data, viajes };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedMonthData = { ...monthData, plan: newData };
    saveMonthData(monthIndex, updatedMonthData, year);
    updateCalendarEvents(newData); // Update calendar when a trip is deleted
  };

  const daysInMonth = getDaysInMonth(monthIndex, year);
  const firstDay = getFirstDayOfMonth(monthIndex, year);

  // Función helper para parsear fechas sin problemas de zona horaria
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    // Parsear fecha en formato YYYY-MM-DD como fecha local (no UTC)
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JS son 0-indexed
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  // Función para actualizar eventos en el calendario basado en viajes
  const updateCalendarEvents = (planData) => {
    const daysInCurrentMonth = getDaysInMonth(monthIndex, year);
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);
    const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
    lastDayOfMonth.setHours(23, 59, 59, 999);
    
    // Separar texto manual de eventos automáticos
    const manualText = {}; // Solo texto manual (sin eventos automáticos)
    const autoEvents = {}; // Solo eventos automáticos nuevos
    
    // Primero, extraer solo el texto manual del calendario actual
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const dayKey = day.toString();
      const currentText = planData.calendario[dayKey] || '';
      const lines = currentText.split('\n');
      const manualLines = lines.filter(line => {
        const trimmed = line.trim();
        // Verificar si es un evento automático (iconos o banderas de países)
        const isAutoEvent = trimmed.startsWith('📍') || 
                           trimmed.startsWith('✈️') || 
                           trimmed.startsWith('🏠') ||
                           trimmed.startsWith('🚂') ||
                           trimmed.startsWith('🚌') ||
                           /^[\u{1F1E6}-\u{1F1FF}]{2}/u.test(trimmed); // Bandera de país (2 emojis)
        return !isAutoEvent;
      });
      if (manualLines.length > 0 && manualLines.join('').trim() !== '') {
        manualText[dayKey] = manualLines.join('\n');
      }
    }
    
    // Primero, eliminar TODOS los eventos automáticos del calendario
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const dayKey = day.toString();
      if (autoEvents[dayKey]) {
        autoEvents[dayKey] = autoEvents[dayKey].filter(e => {
          const trimmed = e.trim();
          // Verificar si es un evento automático (iconos o banderas de países)
          return !trimmed.startsWith('📍') && 
                 !trimmed.startsWith('✈️') && 
                 !trimmed.startsWith('🏠') &&
                 !trimmed.startsWith('🚂') &&
                 !trimmed.startsWith('🚌') &&
                 !/^[\u{1F1E6}-\u{1F1FF}]{2}/u.test(trimmed); // No es bandera de país
        });
      }
    }

    // Crear un mapeo de eventos a índices de viaje para mantener colores consistentes
    const eventToViajeIndex = {}; // Mapea texto del evento a índice del viaje
    
    // Ahora procesar todos los viajes y construir eventos automáticos
    planData.viajes.forEach((viaje, viajeIndex) => {
      // Marcar fechas de viaje
      if (viaje.fechaInicio && viaje.fechaFin) {
        const inicio = parseDateLocal(viaje.fechaInicio);
        const fin = parseDateLocal(viaje.fechaFin);
        
        if (!inicio || !fin) return;
        
        inicio.setHours(0, 0, 0, 0);
        fin.setHours(23, 59, 59, 999);
        
        // Verificar si el viaje se superpone con este mes
        if (inicio <= lastDayOfMonth && fin >= firstDayOfMonth) {
          let startDay, endDay;
          
          if (inicio.getMonth() === monthIndex && inicio.getFullYear() === year) {
            startDay = inicio.getDate();
          } else if (inicio < firstDayOfMonth) {
            startDay = 1;
          } else {
            return;
          }
          
          if (fin.getMonth() === monthIndex && fin.getFullYear() === year) {
            endDay = fin.getDate();
          } else if (fin > lastDayOfMonth) {
            endDay = daysInCurrentMonth;
          } else {
            return;
          }
          
          // Construir el texto del evento con bandera del país si existe
          // Compatibilidad con datos antiguos que usan "destino"
          const nombreViaje = viaje.nombre || viaje.destino || '';
          const paisInfo = viaje.pais ? COUNTRIES.find(c => c.code === viaje.pais) : null;
          const bandera = paisInfo ? paisInfo.flag : '';
          const textoFinal = nombreViaje || (paisInfo ? paisInfo.name : 'Viaje');
          const eventoText = bandera ? `${bandera} ${textoFinal}` : `📍 ${textoFinal}`;
          // Mapear este evento al índice del viaje
          eventToViajeIndex[eventoText] = viajeIndex;
          
          for (let day = startDay; day <= endDay; day++) {
            const dayKey = day.toString();
            if (!autoEvents[dayKey]) {
              autoEvents[dayKey] = [];
            }
            // Solo agregar si no existe ya
            if (!autoEvents[dayKey].includes(eventoText)) {
              autoEvents[dayKey].push(eventoText);
            }
          }
        }
      }

      // Marcar transportes - recopilar todos los transportes de este viaje (compatibilidad con vuelos antiguos)
      const transportes = viaje.transportes || (viaje.vuelos ? viaje.vuelos.map(v => ({
        tipo: 'vuelo',
        fecha: v.fecha,
        compania: v.aerolinea || '',
        numero: v.numeroVuelo || '',
        origen: v.aeropuertoSalida || '',
        destino: v.aeropuertoLlegada || ''
      })) : []);

      transportes.forEach((transporte, transporteIndex) => {
        if (transporte.fecha) {
          const transporteDate = parseDateLocal(transporte.fecha);
          if (transporteDate && transporteDate.getMonth() === monthIndex && transporteDate.getFullYear() === year) {
            const transporteDay = transporteDate.getDate();
            const dayKey = transporteDay.toString();
            
            if (!autoEvents[dayKey]) {
              autoEvents[dayKey] = [];
            }
            
            const tipo = transporte.tipo || 'vuelo';
            const icono = tipo === 'tren' ? '🚂' : tipo === 'bus' ? '🚌' : '✈️';
            
            const transporteInfo = [
              transporte.compania,
              transporte.numero,
              transporte.origen && transporte.destino 
                ? `${transporte.origen}-${transporte.destino}`
                : ''
            ].filter(Boolean).join(' ');
            const transporteText = transporteInfo 
              ? `${icono} ${transporteInfo}`
              : `${icono} ${tipo === 'tren' ? 'Tren' : tipo === 'bus' ? 'Bus' : 'Vuelo'}`;
            
            // Mapear este transporte al índice del viaje
            eventToViajeIndex[transporteText] = viajeIndex;
            
            // Solo agregar si no existe ya (evitar duplicados)
            if (!autoEvents[dayKey].includes(transporteText)) {
              autoEvents[dayKey].push(transporteText);
            }
          }
        }
      });

      // Marcar check-in/check-out de Airbnb
      (viaje.airbnbs || []).forEach((airbnb, airbnbIndex) => {
        if (airbnb.checkIn) {
          const checkInDate = parseDateLocal(airbnb.checkIn);
          if (checkInDate && checkInDate.getMonth() === monthIndex && checkInDate.getFullYear() === year) {
            const checkInDay = checkInDate.getDate();
            const dayKey = checkInDay.toString();
            
            if (!autoEvents[dayKey]) {
              autoEvents[dayKey] = [];
            }
            
            const checkInText = airbnb.nombre 
              ? `🏠 Check-in: ${airbnb.nombre}`
              : '🏠 Check-in';
            
            // Mapear este check-in al índice del viaje
            eventToViajeIndex[checkInText] = viajeIndex;
            
            // Solo agregar si no existe ya
            if (!autoEvents[dayKey].includes(checkInText)) {
              autoEvents[dayKey].push(checkInText);
            }
          }
        }
        if (airbnb.checkOut) {
          const checkOutDate = parseDateLocal(airbnb.checkOut);
          if (checkOutDate && checkOutDate.getMonth() === monthIndex && checkOutDate.getFullYear() === year) {
            const checkOutDay = checkOutDate.getDate();
            const dayKey = checkOutDay.toString();
            
            if (!autoEvents[dayKey]) {
              autoEvents[dayKey] = [];
            }
            
            const checkOutText = airbnb.nombre 
              ? `🏠 Check-out: ${airbnb.nombre}`
              : '🏠 Check-out';
            
            // Mapear este check-out al índice del viaje
            eventToViajeIndex[checkOutText] = viajeIndex;
            
            // Solo agregar si no existe ya
            if (!autoEvents[dayKey].includes(checkOutText)) {
              autoEvents[dayKey].push(checkOutText);
            }
          }
        }
      });
    });

    // Combinar texto manual con eventos automáticos y guardar el mapeo
    const newCalendario = {};
    for (let day = 1; day <= daysInCurrentMonth; day++) {
      const dayKey = day.toString();
      const manual = manualText[dayKey] || '';
      const events = autoEvents[dayKey] || [];
      
      const allLines = [];
      if (manual.trim()) {
        allLines.push(manual);
      }
      allLines.push(...events);
      
      if (allLines.length > 0) {
        newCalendario[dayKey] = allLines.join('\n');
      }
    }
    
    // Guardar el mapeo en el estado para usarlo al renderizar
    const newData = { ...planData, calendario: newCalendario, eventToViajeIndexMap: eventToViajeIndex };
    setData(newData);
    const monthData = loadMonthData(monthIndex, year) || {};
    const updatedMonthData = { ...monthData, plan: newData };
    saveMonthData(monthIndex, updatedMonthData, year);
  };

  // Obtener eventos del día para mostrar badges
  const getDayEvents = (day) => {
    const dayKey = day.toString();
    const dayText = data.calendario[dayKey] || '';
    // Contar viajes: pueden empezar con 📍 o con bandera de país (2 emojis de bandera)
    const viajePattern = /(📍|[\u{1F1E6}-\u{1F1FF}]{2})[^\n]*/gu;
    const viajesMatches = dayText.match(viajePattern);
    const eventos = {
      transportes: (dayText.includes('✈️') || dayText.includes('🚂') || dayText.includes('🚌')) 
        ? (dayText.match(/[✈️🚂🚌][^\n]*/g)?.length || 0) : 0,
      alojamientos: dayText.includes('🏠') ? dayText.match(/🏠[^\n]*/g)?.length || 0 : 0,
      viajes: viajesMatches ? viajesMatches.length : 0
    };
    return eventos;
  };

  const renderCalendar = () => {
    const cells = [];
    
    // Días de la semana
    DAYS_OF_WEEK.forEach(day => {
      cells.push(
        <div key={`header-${day}`} className="calendar-header">
          {day}
        </div>
      );
    });

    // Espacios vacíos antes del primer día
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="calendar-cell empty"></div>);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const dayKey = day.toString();
      const eventos = getDayEvents(day);
      const hasEvents = eventos.transportes > 0 || eventos.alojamientos > 0 || eventos.viajes > 0;
      
      const dayText = data.calendario[dayKey] || '';
      // Filtrar eventos de viaje: pueden empezar con 📍 o con una bandera de país
      const viajeLines = dayText.split('\n').filter(line => {
        const trimmed = line.trim();
        return trimmed.startsWith('📍') || /^[\u{1F1E6}-\u{1F1FF}]{2}/u.test(trimmed);
      });
      const transporteLines = dayText.split('\n').filter(line => 
        line.trim().startsWith('✈️') || line.trim().startsWith('🚂') || line.trim().startsWith('🚌')
      );
      const airbnbLines = dayText.split('\n').filter(line => line.trim().startsWith('🏠'));
      
      const totalEvents = viajeLines.length + transporteLines.length + airbnbLines.length;
      const eventCount = totalEvents || 1; // Mínimo 1 para calcular altura
      
      // Función helper para obtener el índice del viaje desde el texto del evento
      const getViajeIndex = (eventText) => {
        const map = data.eventToViajeIndexMap || {};
        return map[eventText] !== undefined ? map[eventText] : 0;
      };

      // Función helper para obtener el tipo de transporte desde el icono
      const getTransporteType = (line) => {
        if (line.trim().startsWith('🚂')) return 'tren';
        if (line.trim().startsWith('🚌')) return 'bus';
        return 'vuelo';
      };
      
      cells.push(
        <div 
          key={`day-${day}`} 
          className={`calendar-cell ${hasEvents ? 'has-events' : ''}`}
          style={{
            '--event-count': eventCount,
            minHeight: `${Math.max(200, eventCount * 30)}px`
          }}
        >
          {hasEvents && (
            <div className="event-bars-container">
              {viajeLines.map((line, idx) => {
                const viajeIndex = getViajeIndex(line.trim());
                // Extraer bandera y texto del evento
                const trimmedLine = line.trim();
                // Buscar si tiene bandera (emoji de bandera) o icono de ubicación
                const hasFlag = /[\u{1F1E6}-\u{1F1FF}]{2}/u.test(trimmedLine);
                let icono, text;
                
                if (hasFlag) {
                  // Extraer bandera (2 caracteres emoji)
                  const flagMatch = trimmedLine.match(/[\u{1F1E6}-\u{1F1FF}]{2}/u);
                  icono = flagMatch ? flagMatch[0] : '📍';
                  text = trimmedLine.replace(/[\u{1F1E6}-\u{1F1FF}]{2}/u, '').trim() || 'Viaje';
                } else {
                  // Compatibilidad con eventos antiguos que usan 📍
                  icono = '📍';
                  text = trimmedLine.replace('📍', '').trim() || 'Viaje';
                }
                
                return (
                  <div 
                    key={`viaje-${idx}`} 
                    className={`event-bar viaje-bar viaje-bar-${viajeIndex % 3}`}
                    title={line.trim()}
                  >
                    <span className="event-icon">{icono}</span>
                    <span className="event-label">{text}</span>
                  </div>
                );
              })}
              {transporteLines.map((line, idx) => {
                const viajeIndex = getViajeIndex(line.trim());
                const tipo = getTransporteType(line);
                const icono = tipo === 'tren' ? '🚂' : tipo === 'bus' ? '🚌' : '✈️';
                const defaultText = tipo === 'tren' ? 'Tren' : tipo === 'bus' ? 'Bus' : 'Vuelo';
                const text = line.trim().replace(/[✈️🚂🚌]/, '').trim() || defaultText;
                return (
                  <div 
                    key={`transporte-${idx}`} 
                    className={`event-bar transporte-bar transporte-bar-${tipo} transporte-bar-${viajeIndex % 4}`}
                    title={line.trim()}
                  >
                    <span className="event-icon">{icono}</span>
                    <span className="event-label">{text}</span>
                  </div>
                );
              })}
              {airbnbLines.map((line, idx) => {
                const viajeIndex = getViajeIndex(line.trim());
                const text = line.trim().replace('🏠', '').trim() || 'Alojamiento';
                const isCheckIn = line.includes('Check-in');
                const isCheckOut = line.includes('Check-out');
                const icono = isCheckIn || isCheckOut ? '🏠' : '🏠';
                return (
                  <div 
                    key={`airbnb-${idx}`} 
                    className={`event-bar airbnb-bar airbnb-bar-${viajeIndex % 3}`}
                    title={line.trim()}
                  >
                    <span className="event-icon">{icono}</span>
                    <span className="event-label">{text}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="calendar-day-header">
            <div className="calendar-day-number">{day}</div>
          </div>
        </div>
      );
    }

    return cells;
  };

  return (
    <div className="month-plan">
      <header className="plan-header">
        <h1 className="month-title">{monthName} {year}</h1>
      </header>

    

      <section className="calendar-section">
                <div className="calendar-grid">
          {renderCalendar()}
        </div>
      </section>

      <section className="planificacion-section">
        <div className="section-header">
          <div className="section-title-with-icon">
            <span className="section-icon floating-delayed">✈️</span>
            <h2 className="section-title-text">Planificación de Viajes</h2>
          </div>
          <button 
            type="button" 
            className="add-viaje-btn pulse-on-hover"
            onClick={addViaje}
          >
            <span className="btn-icon">➕</span>
            <span>Agregar Viaje</span>
          </button>
        </div>

        {data.viajes && data.viajes.length > 0 ? (
          data.viajes.map((viaje, index) => (
            <Viaje
              key={index}
              viaje={viaje}
              index={index}
              onChange={handleViajeChange}
              onDelete={deleteViaje}
              monthIndex={monthIndex}
              year={year}
            />
          ))
        ) : (
          <div className="empty-state">
            <p>No hay viajes planificados para este mes.</p>
            <p className="empty-state-hint">Haz clic en "Agregar Viaje" para comenzar.</p>
          </div>
        )}
      </section>

      <section className="ideas-section">
        <div className="section-title-with-icon">
          <span className="section-icon floating-delayed-2">🌍</span>
          <h2 className="section-title-text">Destinos a Investigar</h2>
        </div>
        <StyledTextarea
          value={data.ideasFuturas}
          onChange={(value) => handleChange('ideasFuturas', value)}
          label=""
          placeholder="Escribe aquí destinos que te gustaría investigar..."
          rows={3}
          variant="ideas"
        />
      </section>
    </div>
  );
};

export default MonthPlan;
